import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { History } from 'history';

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

/**
 * Telemetry Service interface
 */
interface TelemetryService {
    reactPlugin: ReactPlugin | null;
    appInsights: ApplicationInsights | null;
    initialize: (instrumentationKey: string, browserHistory: History) => void;
}

/**
 * Create the App Insights Telemetry Service
 * @return {TelemetryService} - Telemetry service object
 */
const createTelemetryService = (): TelemetryService => {

    /**
     * Initialize the Application Insights class
     * @param {string} instrumentationKey - Application Insights Instrumentation Key
     * @param {History} browserHistory - client's browser history, supplied by the withRouter HOC
     * @return {void}
     */
    const initialize = (instrumentationKey: string, browserHistory: History): void => {
        if (!browserHistory) {
            throw new Error('Could not initialize Telemetry Service: Browser history is required');
        }
        if (!instrumentationKey) {
            throw new Error('Could not initialize Telemetry Service: Instrumentation key is required');
        }

        // Prevent multiple initializations
        if (appInsights) {
            console.warn('Application Insights telemetry service is already initialized');
            return;
        }

        try {
            reactPlugin = new ReactPlugin();

            appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey: instrumentationKey,
                    maxBatchInterval: 0,
                    disableFetchTracking: false,
                    enableAutoRouteTracking: true,
                    // Disable React error boundary tracking to prevent null reference errors
                    enableUnhandledPromiseRejectionTracking: false,
                    disablePageUnloadEvents: true, // Prevents attaching to 'unload' event
                    extensions: [reactPlugin],
                    extensionConfig: {
                        [reactPlugin.identifier]: {
                            history: browserHistory,
                            // Add error boundary configuration
                            errorBoundaryOptions: {
                                enableErrorBoundary: false
                            }
                        }
                    }
                }
            });

            appInsights.loadAppInsights();
            
            // Track page views for SPA
            appInsights.trackPageView();
            
            console.log('Application Insights telemetry service initialized successfully');
        } catch (error) {
            console.error('Failed to initialize Application Insights telemetry service:', error);
            throw error;
        }
    };

    return { reactPlugin, appInsights, initialize };
};

export const ai = createTelemetryService();
export const getAppInsights = (): ApplicationInsights | null => appInsights;
